import "./tenant-new.scss";
import React from "react";
import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";

import ListPage from "../../../shared/components/list-page/list-page";
import { ITableRowData, IColumn } from "../../../shared/components/normal-table/normal-table";
import Spinner from "../../../shared/components/spinner/spinner";
import { roleAdmin } from "../../../shared/constants/user-roles";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import {useHttpNonTable} from "../../../shared/hooks/use-httpNonTable";
import { postSingleTenant, getAllDataNonTable, deleteMultiple } from "../../../shared/utils/api";
import { createUUID } from "../../../shared/utils/utils";
import { validateLoadingStatusNew } from "../../../shared/utils/utils";


const columns: IColumn[] = [
  {
    key: "name",
    displayName: "First Name",
    dataType: {
      type: "text",
    },
    nonSortable: true
  },
  {
    key: "last_name",
    displayName: "Last Name",
    dataType: {
      type: "text",
    },
    nonSortable: true
  },
  {
    key: "email",
    displayName: "Email Address",
    dataType: {
      type: "text",
    },
    nonSortable: true
  },
  {
    key: "roles",
    displayName: "Roles",
    dataType: {
      type: "text",
    },
    nonSortable: true,
    noWrap: true
  },
];

const TenantNew = () => {
  const [data, setData] = useState({rows: {users: []}});
  const [inputData, setInputData] = useState("");
  const [columsData, setColumsData] = useState<IColumn[]>([]);
  const [toggleWarning, setToggleWarning] = useState(false);

  const [unblockInput, setUnblockInput] = useState(false);
  const authCtx = useContext(AuthContext);
  const params = useParams();
  const navigate = useNavigate();
  
  const { sendRequest: getSortBy, status: getSortByStatus, data: getSortByData, error: errorGetSortByData } = useHttpNonTable(getAllDataNonTable, false, authCtx.token, "tenant");
  const { sendRequest: getSingleDataRequest, status: allStatus, data: loadedData, error: errorGet } = useHttpNonTable(getAllDataNonTable, false, authCtx.token, "tenant");
  const { sendRequest: postSingleRequest, status: allStatusPost, data: loadedDataPost, error: errorPost } = useHttpNonTable(postSingleTenant, false, authCtx.token, "tenant");
  const { sendRequest: deleteMultipleRequest, status: allStatusDelete, data: loadedDataDelete, error: errorDelete } = useHttp(deleteMultiple, false, authCtx.token);
  
  useEffect(() => {
    setColumsData(columns);
  }, []);

  useEffect(() => {
    const rolesArray = authCtx.selectedTenant.roles;
    if (authCtx.systemAdmin || rolesArray.indexOf(roleAdmin) !== -1) {
      setUnblockInput(true);
    }
  }, [authCtx.selectedTenant, authCtx.systemAdmin]);

  useEffect(() => {
    if (allStatus === "completed" && !errorGet) {
      const result = loadedData.users.map((user:ITableRowData) => {
        return {...user, roles: !user.roles ? [] : user.roles};
      });
      setData({rows: {...loadedData, users: result}});
      loadedData && setInputData(loadedData.name);
    }
  }, [loadedData]);

  useEffect(() => {
    if (getSortByStatus === "completed" && !errorGetSortByData) {
      setData({rows: {...getSortByData}});
    }
  }, [getSortByStatus]);

  useEffect(() => {
    if (allStatusDelete === "completed" && !errorDelete) {
      authCtx.userUpdate();
      setData({rows: loadedDataDelete});
      loadedData && setInputData(loadedDataDelete.name);
    }
  }, [allStatusDelete]);

  useEffect(() => {
    if (allStatusPost === "completed" && !errorPost) {
      authCtx.userUpdate();
      setData({rows:loadedDataPost});
      loadedData && setInputData(loadedDataPost.name);
    }
  }, [loadedDataPost]);

  useEffect(() => {
    if (params.id !== "new" && params.id) {
      getSingleDataRequest(`tenant/${params.id}`);
    }
  }, [getSingleDataRequest]);

  useEffect(() => {
    if (inputData !== "" && toggleWarning) {
      setToggleWarning(false);
    }
  }, [inputData]);

  const getInputValue = (value: string) => {
    unblockInput && setInputData(value);
  };

  const saveData = async (rowData: ITableRowData[], ids?: string[]) => {
    setData({rows: {...loadedData, users: rowData}});

    if (inputData === "") {
      setToggleWarning(true);
    }
    
    if (inputData !== "") {
      const users = rowData.map(row => ({id: row.id, roles: row.roles }));
      if (params.id) {
        if (ids && ids.length !== 0) { 
          deleteMultipleRequest(`tenant/${params.id}`, { ids });
        }
        postSingleRequest(`tenant/${params.id}`, { id:params.id, name: inputData, users: users });
      } else {
        const createdId = createUUID();
        postSingleRequest(`tenant/${createdId}`, {id:createdId, name: inputData, users: users });
      }
    }
  };

  const sortBy = (sortingQuery: string) => {
    getSortBy(`tenant/${params.id}`, `${sortingQuery}`);
  };

  const renderPathButtons = () => {
    return (
      <span >
        <span onClick={() => navigate("/tenants")} className="optionPath">Tenants</span>
        <span className="optionPathDelimiter">-</span>
        <span className="selectedPathBtn">Tenant</span>
      </span>
    );
  };

  return (
    <div className="TenantNew">
      {validateLoadingStatusNew([allStatusPost, getSortByStatus, allStatus]) && <Spinner />}
      <ListPage
        fetchedData={{rowData: data.rows.users}}
        title={"New Tenant"}
        columns={columsData}
        sortBy={sortBy}
        unblockInput={unblockInput}
        dataUrl="tenants/new"
        pageMode={"editable"}
        renderPathButtons={params.id? renderPathButtons : undefined}
        getInputValue={getInputValue}
        saveData={saveData}
        getData={() => {/**/}}
        allStatusPost={allStatusPost}
        allStatus={allStatus}
        allStatusPostError={errorPost}
        allStatusError={errorGet}
        toggleWarning={toggleWarning}
        localDeleteOn={true}
        inputData={inputData}
        navigateToAfterSave={"/tenants"}
        hideButtons={["generate"]}
        titleIsEditable={false}
      />
    </div>
  );
};

export default TenantNew;
